import { useCallback, useEffect, useState } from 'react';

/**
 * useDialog — кастомный React-хук для управления состоянием модальных окон.
 *
 * Позволяет открывать/закрывать любые диалоговые/модальные окна с помощью состояния isOpen.
 * Добавляет обработчик Escape для закрытия модалки.
 *
 * @param {boolean} initialOpen — Опционально: начальное состояние модалки (по умолчанию false).
 * @param {function} onCloseCb — Опционально: функция, которая отрабатывает после закрытия модалки (например, очищения стейта для модалок).
 * @returns {{
 *   isOpen: boolean,        // Состояние открытия модалки
 *   open: () => void,       // Функция открытия модалки
 *   close: () => void       // Функция закрытия модалки
 * }}
 *
 * @example
 * const { isOpen, open, close } = useDialog();
 * <CustomModal isOpen={isOpen} onClose={close}>...</CustomModal>
 * <UiButton onClick={open}>Открыть модалку</UiButton>
 *
 * // Модалка закроется по кнопке, по вызову close() и по нажатию Escape.
 */

export const useDialog = (
    initialOpen?: boolean,
    onCloseCb?: () => void
): {
    isOpen: boolean;
    open: () => void;
    close: () => void;
} => {
    const [isOpen, setIsOpen] = useState<boolean>(initialOpen ?? false);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        if (!isOpen) return () => {};

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close();
                onCloseCb?.();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, close, onCloseCb]);

    return { isOpen, open, close };
};
